// src/components/map/FullscreenMap.jsx
import React, { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer } from "react-leaflet";
import { X } from "lucide-react";
import Route from "./Route";
import MapController from "./MapController";
import { INDIAN_STATES } from "@/utils/constants";
import SelectDropdown from "../common/SelectDropdown";
import Button from "../common/Button";
import { useMap as useMapHook } from "@/hooks/useMap";

const FullscreenMap = () => {
  const [searchParams] = useSearchParams();
  const stateFromUrl = searchParams.get("state");

  const {
    selectedState,
    mapCenter,
    mapZoom,
    filteredRoutes,
    isLoading,
    error,
    handleStateChange,
  } = useMapHook();

  const [focusedMapCenter, setFocusedMapCenter] = React.useState(mapCenter);
  const [focusedMapZoom, setFocusedMapZoom] = React.useState(mapZoom);

  // Update focused map when mapCenter/mapZoom changes
  useEffect(() => {
    setFocusedMapCenter(mapCenter);
    setFocusedMapZoom(mapZoom);
  }, [mapCenter, mapZoom]);

  const stateName = INDIAN_STATES.find((s) => s.code === selectedState)?.name;

  // Function to calculate center of route from coordinates
  const calculateRouteCenter = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return null;

    const sum = coordinates.reduce(
      (acc, [lng, lat]) => {
        acc.lat += lat;
        acc.lng += lng;
        return acc;
      },
      { lat: 0, lng: 0 }
    );

    return [sum.lat / coordinates.length, sum.lng / coordinates.length];
  };

  // Handle route click to focus map
  const handleRouteFocus = (route) => {
    const center = calculateRouteCenter(route.coordinates);
    if (center) {
      setFocusedMapCenter(center);
      setFocusedMapZoom(12);
    }
  };

  // Set state from URL parameter on component mount
  useEffect(() => {
    if (stateFromUrl && stateFromUrl !== selectedState) {
      handleStateChange(stateFromUrl);
    }
  }, [stateFromUrl, selectedState, handleStateChange]);

  const closeTab = () => {
    window.close();
  };

  // Navigation to route details when a route is clicked
  const navigate = useNavigate();

  const onRouteClick = (route) => {
    if (route?.slug) {
      navigate(`/routes/${route.slug}`);
    }
  };

  const dropdownOptions = INDIAN_STATES.map((s) => ({
    value: s.code,
    label: s.name,
  }));

  return (
    <div className="h-screen w-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors duration-300">
      {/* Top Controls Bar */}
      <div className="bg-white dark:bg-gray-800 shadow-md p-4 flex items-center justify-between z-10 transition-colors duration-300">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">
            Koset - Cycling Routes Map
          </h1>
          <SelectDropdown
            label=""
            options={dropdownOptions}
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            placeholder="Select State"
            className="w-48 !mb-0"
          />
        </div>

        <Button
          onClick={closeTab}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-300"
        >
          <X className="w-4 h-4" />
          <span>Close</span>
        </Button>
      </div>

      {/* Fullscreen Map - Always Visible */}
      <div className="flex-1 relative">
        <MapContainer
          center={focusedMapCenter}
          zoom={focusedMapZoom}
          className="w-full h-full"
          zoomControl={true}
          style={{ zIndex: 0 }}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController center={focusedMapCenter} zoom={focusedMapZoom} />

          {!isLoading &&
            filteredRoutes.map((route, idx) => (
              <Route
                key={route.id}
                route={route}
                index={idx}
                onRouteClick={() => onRouteClick(route)}
              />
            ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default FullscreenMap;
